/* eslint-disable no-unused-vars */
export interface IBenefitType {
  id?: string;
  typeAlias: string;
  type: string;
  generalCategory: string;
  imgURL?: string;
  priceType: 'Fixed' | 'Table';
  versionId: string;
  agePriceLines?: {
    id?: string;
    ageStart: string;
    ageEnd: number;
    EE: number;
    EE_SMOKER_PREMIUM: number;
    EC: number;
    ES: number;
    ES_SMOKER_PREMIUM: number;
    FAM: number;
  }[];
  typeBenefit: {
    benefitId: string;
    costCalculationMethod: 'Fixed' | 'Table' | 'Limited';
  }[];
}

export interface IBenefit {
  id: string;
  name?: string;
  benefitId?: string;
  plan?: string;
  planDescription?: string;
  option?: string;
  optionDescription?: string;
  type?: string;
  effective?: string;
  expiration?: string;
}

export interface IBenefitListTable extends Omit<IBenefit, 'effective' | 'expiration'> {
  rates: {
    value: string;
    columns: string[];
    rows: string[][];
  };
  status: string;
  waitingSync: string;
}

export interface IEnrollmentEvent {
  id?: string;
  name: string;
  eventStartDate: string;
  eventEndDate: string;
  planInitialPeriod: string;
  planEndPeriod: string;
  createdAt?: string;
  status?: string;
  disclaimer?: boolean;
  message: string;
}

export interface IQleNewHireEnrollmentEvent {
  id?: string;
  planInitialPeriod: string;
  planEndPeriod: string;
  createdAt: string;
  createdBy?: {
    id: string;
    username: string;
    email: string;
  };
  updatedAt?: string;
  updatedBy?: {
    id: string;
    username: string;
    email: string;
  };
}

export interface IBenefitsVersionManagement {
  id: string;
  benefitId: string;
  plan: string;
  effectiveDate: string;
  expiration: string;
  amountOrRate: number;
}

export interface IBenefitFixedPrice {
  id?: string;
  versionId: string;
  benefitId: string;
  startDate?: string | Date;
  endDate?: string | Date;
  coverage: IBenefitCoverageType;
  fixedPriceLines: { id?: string; benefitId: string; deduction: number; contribution: number }[];
}

export interface IBenefitAgePrice {
  id?: string;
  versionId: string;
  benefitId: string;
  startDate?: string | Date;
  endDate?: string | Date;
  smoke: boolean;
  type: string;
  coverage: IBenefitCoverageType;
  agePriceLines?: {
    id?: string;
    ageStart: string;
    ageEnd: number;
    EE: number;
    EE_SMOKER_PREMIUM: number;
    EC: number;
    ES: number;
    ES_SMOKER_PREMIUM: number;
    FAM: number;
  }[];
}

export interface IBenefitLimitedPrice {
  id?: string;
  versionId: string;
  benefitId: string;
  type?: string;
  minLimit: number | null;
  maxLimit: number | null;
}

export enum CoverageTypeEnum {
  NO_COVERAGE = 'noCoverage',
  FIXED_COVERAGE_AMOUNT = 'fixedCoverage',
  FIXED_COVERAGE_AMOUNT_SALARY_BASED = 'fixedCoverageAmountSalaryBased',
  ELECTED_COVERAGE_AMOUNT_WITH_FIXED_MAXIMUM = 'electedCoverageAmountWithFixedMaximum',
  ELECTED_COVERAGE_AMOUNT_WITH_RULE_BASED_MAXIMUM = 'ElectedCoverageAmountWithRuleBasedMaximum',
}
export interface IBenefitCoverageType {
  coverageType: CoverageTypeEnum;
  employeeCoverageAmount: number;
  options?: IBenefitCoverageFixedMaximum | IBenefitCoverageSalaryBased | IBenefitCoverageRuleMaximum | {};
}

export interface IBenefitCoverageSalaryBased {
  level: number;
  timesAnnualSalaryUpTo: number;
  reduceCoverageAmountMaxBy: number;
  roundCoverage: string;
  annualSalaryUpTo: number;
}

export interface IGiConfig {
  ageStart: number | string;
  ageEnd: number | string;
  giAmount: number | string;
}
export interface IBenefitCoverageFixedMaximum {
  employeeMaxFixed: number;
  giEnabled: boolean;
  giConfig: IGiConfig[];
  roundCoverage: string;
  roundToNearFixed: number;
  minimumCoverage: number;
}

export interface IBenefitCoverageRuleMaximum {
  employeeMaxRuleBased: number;
  giEnabled: boolean;
  giConfig: IGiConfig[];
  salaryUpToRuleBased: number;
  roundCoverage: string;
  roundToNearRuleBased: number;
  minimumCoverage: number;
}

// Premiums Interface
export interface IFixedCostPremium {
  costMethod: string;
  employeeCost: string | number;
  spouseCost: string | number;
  childCost: string | number;
}

export interface ICoverageAmountBased {
  employeeCost: string | number;
  employeeCostTimes: string | number;
  spouseCost: string | number;
  spouseCostTimes: string | number;
  childCost: string | number;
  childCostTimes: string | number;
  additionalAmountForSmokers: string | number;
  additionalAmountForSmokersTimes: string | number;
  minimumPremiumAmount: string | number;
}

export interface IBasedOnCoverageAmountAndAge {
  employeeCost: string | number;
  spouseCost: string | number;
  childCost: string | number;
  childCostTimes: string | number;
  additionalAmountForSmokers: string | number;
  openEnrollmentDateOption: string;
  openEnrollmentDate: string | Date;
  newHireQLEDate: string | Date;
  newHireQLEDateOption: string;
  useEmployeeAgeForSpousePremium: boolean;
}

// Employer Provide Quantity Interfaces
export interface INoAssistance {
  nothing: boolean;
}

export interface IFixedPortionOfTheEmployeePremium {
  employerAssistance: string;
}

export interface IPercentageOfTheEmployeePremium {
  employerAssistance: string;
}

export interface IFullPremiumAmount {
  fullPremiumAmount: boolean;
}

export interface IPremiumPayoutConfig {
  id?: string;
  versionId: string;
  benefitType: string;
  frequencyOfEmployeeBenefitPayments: string;
  percentageOfEmployeeSalaryPaidByPlan: number | string;
  roundPercentageOfsalary: string;
  roundPercentageOfsalaryUpTo: string | number;
  describeAnyWaitingPeriodDetails: string;
  describeAnyDetailsAboutBenefitDuration: string;
  premiumType: 'fixedCost' | 'coverageAmountBased' | 'basedOnCoverageAmountAndAge';
  premium: IFixedCostPremium | ICoverageAmountBased | IBasedOnCoverageAmountAndAge;
  payrollBenefitCode: string;
  payrollDeductionCode: string;
  employerProvidedQuantityType: 'nothing' | 'fixed' | 'percentage' | 'full';
  employerProvidedQuantity:
    | INoAssistance
    | IFixedPortionOfTheEmployeePremium
    | IPercentageOfTheEmployeePremium
    | IFullPremiumAmount;
}

export interface IBenefitsConfigVersion {
  id?: string;
  type: string | string[];
  version?: string;
  startDate: string | Date;
  endDate: string | Date;
  payrollStartDate: string | Date;
  payrollEndDate: string | Date;
  description: string;
  event: string | string[];
}

export interface IBenefitsDocument {
  id?: string;
  planDocument: string;
  type?: string;
  benefitFile?: string;
  fileName?: string;
  fileSize?: string;
  createdAt?: string;
}
